// @flow
import React, { Component } from 'react';
import { connect } from 'react-redux';
import * as Cookie from 'app/packages/cookie-utils';
import autoLoginLoader from 'app/www-unauth/components/autoLoginLoader';
import contextEvent from 'app/common/lib/ContextEvent';
import contextLogger from 'app/common/lib/ContextLogger';
import DeeplinkAutoRedirect from 'app/common/lib/react/DeeplinkAutoRedirect';
import EuCookieBar from 'app/common/react/components/growth/unauth/header/EuCookieBar/EuCookieBar';
import InternalHybridHelperLoader from 'app/common/react/components/growth/unauth/internal/InternalHybridHelper/InternalHybridHelperLoader';
import loginUtil from 'app/www-unauth/lib/loginUtil';
import logPerfTimings from 'app/common/lib/logPerfTimings';
import PropTypes from 'prop-types';
import ResourceFactory from 'app/common/lib/ResourceFactory';
import storage from 'app/common/lib/Storage';
import trackRegisterAction from 'app/common/lib/util/trackRegisterAction';
import type { Deeplinks } from 'app/common/lib/deeplinkRoutes';
import type { State as reduxState } from 'app/www-unauth/redux/types';
import { activateGoogleSignInExperiment } from 'app/common/react/components/growth/unauth/signup/GoogleConnectButton/googleSignInExperimentUtil';
import { compose } from 'redux';
import { MultiTreatmentExp } from 'app/common/lib/experiment';
import { parseQuery } from 'app/packages/url';
import withUnauthSeoExperiments, {
  type UnauthSeoExperiments,
} from 'app/common/lib/react/withUnauthSeoExperiments';
import {
  withRequestContext,
  type RequestContextType,
} from 'app/common/react/components/RequestContext';

type Route = {
  deeplinks?: Deeplinks,
  legacy_server_context: {
    is_internal_ip: boolean,
    invite_sender_experiments: any,
  },
  pageType: string,
  path?: string,
};

type Location = {
  pathname: string,
  query?: {
    description?: string,
    media?: string,
    url?: string,
    q?: string,
    next?: string,
  },
  search: string,
};

type Props = {
  children: any,
  dispatch: Function,
  location: Location,
  params: {},
  route: Route,
  routes: Array<Route>,
  seoUnauthExperiments: UnauthSeoExperiments,
  showLoginModal: boolean,
  isShowEUCookieBar: boolean,
  requestContext: RequestContextType,
};

type Context = {
  cookies: Object,
  country?: string,
  isAuthenticated: boolean,
  isBot?: boolean,
  userAgent: {
    isMobile: boolean,
    isTablet: boolean,
  },
};

type RevisitationType = {|
  count?: number,
  time: number,
|};

const divStyle = {
  marginTop: '36px',
};

const AUTOLOGIN_BLACKLIST_PAGES = ['passwordReset', 'passwordUserName'];

export class App extends Component<Props> {
  constructor(props: Props, context: Context) {
    super(props, context);

    const { routes } = this.props;
    const route = routes[1];
    this.pageType = route ? route.pageType : '';
  }

  componentDidMount() {
    const { dispatch, location, requestContext, routes } = this.props;
    const {
      userAgent: { isMobile, isTablet },
    } = this.context;
    const invite_code = parseQuery(location.search).invite_code || null;

    if (invite_code && this.pageType === 'pinFeedBack') {
      ResourceFactory.create('InviteCodeMetadataResource', { invite_code })
        .callGet()
        .then((response) => {
          if (response && response.resource_response) {
            const invite = response.resource_response.data;
            dispatch({
              type: 'UPDATE_INVITER_DATA',
              payload: invite,
            });
          }
        })
        .catch((err) => {
          dispatch({
            type: 'CLEAR_INVITER_DATA',
          });
        });
    }

    if (!AUTOLOGIN_BLACKLIST_PAGES.includes(this.pageType)) {
      this.attemptAutologin(this.pageType, requestContext, dispatch, location);
    }

    if (isTablet) {
      trackRegisterAction('unauth_web.desktop_app_mounted_in_tablet');
    } else if (isMobile) {
      trackRegisterAction('unauth_web.desktop_app_mounted_in_mobile');
    }

    // (javery) Activating globally to address experiment imbalances
    // (https://analytics.pinadmin.com/experiments/web_unauth_gplus_deprecation/unauth/)
    activateGoogleSignInExperiment(requestContext);

    const register = () => {
      if (navigator.serviceWorker) {
        if (navigator.serviceWorker && navigator.serviceWorker.register) {
          navigator.serviceWorker.register('/sw.js');
        }
      }
    };
    if (document.readyState === 'complete') {
      register();
    } else {
      window.addEventListener('load', register);
    }
    const cookieEnabled = navigator.cookieEnabled;
    if (!cookieEnabled) {
      trackRegisterAction('unauth_disabled_cookie');
    }

    loginUtil.fetchRecentlyLoggedOutUser(this.context).then(
      (userInfo) => {
        if (!!userInfo) {
          dispatch({
            type: 'LOGGED_OUT_USER_INFO_FOUND',
            payload: userInfo,
          });
        }
      },
      (failure) => {
        dispatch({
          type: 'LOGGED_OUT_USER_INFO_NOT_FOUND',
        });
      },
    );

    if (!this.context.isBot) {
      let revisitation = storage.localStorage.getItem(PCONST.LOCALSTORAGE_UNAUTH_REVISITATION);
      if (!revisitation || revisitation.count < 1 || !revisitation.time) {
        revisitation = this.defaultRevisitation();
        this.setUnauthSession(revisitation);
      } else if (new Date().getTime() - revisitation.time > 600000) {
        // if time difference > 10 minutes, consider it a new session
        revisitation.count += 1;
        revisitation.time = new Date().getTime();
        this.setUnauthSession(revisitation);
      }

      window.addEventListener('unload', this.setUnauthSessionEnd);

      this.logUnauthSessionHealth(revisitation);
    }
    const routePath = routes
      .map((route) => route.path || '/')
      .join('/')
      .replace(/[\/]+/g, '/');
    logPerfTimings(routePath);
  }

  pageType: string;

  componentWillUnmount() {
    window.removeEventListener('unload', this.setUnauthSessionEnd);
  }

  attemptAutologin(
    pageType: string,
    requestContext: RequestContextType,
    dispatch: Function,
    location: Location,
  ) {
    if (pageType === 'board') {
      // Unauth board page autologin happens in Main.js
      // Do nothing here
    } else if (pageType === 'pin') {
      const inPLPPerfAutoImproExp = new MultiTreatmentExp(
        requestContext,
        'web_unauth_plp_python_resource_prefetch',
      ).check('enabled_early_auto');
      // If in exp, unauth pin page autologin happens in Main.js
      if (!inPLPPerfAutoImproExp) {
        autoLoginLoader.attemptLogin(requestContext, dispatch, location);
      }
    } else {
      autoLoginLoader.attemptLogin(requestContext, dispatch, location);
    }
  }

  defaultRevisitation = () => ({ count: 1, time: new Date().getTime() });

  setUnauthSession = (revisitation: RevisitationType) => {
    storage.localStorage.setItem(PCONST.LOCALSTORAGE_UNAUTH_REVISITATION, revisitation);
  };

  setUnauthSessionEnd = () => {
    const revisitation =
      storage.localStorage.getItem(PCONST.LOCALSTORAGE_UNAUTH_REVISITATION) ||
      this.defaultRevisitation();
    revisitation.time = new Date().getTime();
    this.setUnauthSession(revisitation);
  };

  logUnauthSessionHealth(revisitation: RevisitationType) {
    if (!__SERVER__) {
      const options = {
        unauth_revisitation: revisitation.count,
        unauth_previously_logged_out: Cookie.get(PCONST.COOKIE_FB_AUTO_LOGIN_DISABLED),
      };
      const eventDict = {
        event_type: PCONST.thrift.EventType.UNAUTH_SESSION_HEALTH,
        aux_data: options,
      };
      const _contextEvent = contextEvent.fromPlainObject(eventDict);
      contextLogger.getInstance().addEvent(_contextEvent);
    }
  }

  div: ?HTMLElement;

  buildRouteDeepLinks() {
    const { params, location, routes } = this.props;
    const route = routes.slice(-1)[0];
    if (!route || !route.deeplinks) {
      return {};
    }

    const query = (location && location.query) || '';
    return route.deeplinks(params, query);
  }

  render() {
    const { children, route } = this.props;
    const { androidDeeplink, iOSDeeplink } = this.buildRouteDeepLinks();
    const isInternalIp = route.legacy_server_context.is_internal_ip;

    return (
      <div
        ref={(div) => {
          this.div = div;
        }}
      >
        <DeeplinkAutoRedirect androidDeeplink={androidDeeplink} iOSDeeplink={iOSDeeplink} />
        <EuCookieBar dispatch={this.props.dispatch} pageType={this.pageType} />
        <div style={this.props.isShowEUCookieBar ? divStyle : null}>{children}</div>
        {isInternalIp && <InternalHybridHelperLoader country={this.context.country} />}
      </div>
    );
  }
}

App.contextTypes = {
  cookies: PropTypes.object.isRequired,
  country: PropTypes.string,
  isAuthenticated: PropTypes.bool.isRequired,
  userAgent: PropTypes.shape({
    isMobile: PropTypes.bool.isRequired,
    isTablet: PropTypes.bool.isRequired,
  }).isRequired,
};

export default compose(
  withRequestContext,
  withUnauthSeoExperiments,
  connect((state: reduxState) => ({
    showLoginModal: state.ui.login.showModal,
    isShowEUCookieBar: state.euCookieBar.isShowEUCookieBar,
  })),
)(App);
